import {
  forwardRef,
  type ElementType,
  type ForwardedRef,
  type ReactNode,
} from "react";

import { cn } from "shared/lib";

interface Props {
  className?: string;
  icon: ElementType;
  label: ReactNode;
  onSelect: () => void;
  onSelectNext?: () => void;
  onSelectPrevious?: () => void;
  selected: boolean;
  showUpdateIcon?: boolean;
  shouldAnimate?: boolean;
}

function InnerTabItem(
  {
    className,
    icon,
    label,
    onSelect,
    onSelectNext,
    onSelectPrevious,
    selected,
    showUpdateIcon = false,
    shouldAnimate,
  }: Props,
  ref: ForwardedRef<HTMLButtonElement>,
) {
  const Icon = icon;

  return (
    <button
      aria-label={`${label} tab`}
      aria-selected={selected}
      className={cn(
        "text-textSubdued cursor-pointer text-bodylg font-medium",
        "border-t-3 flex flex-1 flex-col items-center justify-center gap-1 border-transparent py-3",
        shouldAnimate && selected && "animate-wiggle",
        selected && "border-t-textLink text-textLink font-semibold",
        className,
      )}
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          onSelect();
        } else if (event.key === "ArrowRight" && onSelectNext) {
          event.preventDefault();
          onSelectNext();
        } else if (event.key === "ArrowLeft" && onSelectPrevious) {
          event.preventDefault();
          onSelectPrevious();
        }
      }}
      ref={ref}
      role="tab"
      tabIndex={selected ? 0 : -1}
    >
      <div className="relative flex flex-col items-center justify-center">
        <Icon className="h-[18px] w-[18px]" />
        {showUpdateIcon ? (
          <div className="bg-actionDefault absolute -right-2 top-0 h-1.5 w-1.5 rounded-full md:hidden" />
        ) : null}
      </div>
      <div className="relative flex flex-col items-center justify-center">
        {label}
        {showUpdateIcon ? (
          <div className="bg-actionDefault -right-1.5 top-0 hidden h-1.5 w-1.5 rounded-full md:absolute md:flex" />
        ) : null}
      </div>
    </button>
  );
}

export const TabItem = forwardRef(InnerTabItem);
